import {
  validEmail,
  validName,
  validPhoneNumber,
  addCountryCode,
} from "src/utils";
import type { InviteLender } from "./InvitePage";
import type {
  DtoApplicationInviteReq,
  DomainLender,
} from "@porchpass/api-client";
import { API_PREFIX_PATH } from "src/constants";
import type { FetchFromApiType } from "src/hooks/api/useApiClient";

export const validateField = (
  field: "firstName" | "lastName" | "emailOrPhone",
  value: string,
) => {
  switch (field) {
    case "firstName":
      return validName(value) ? "" : "Invalid first name";
    case "lastName":
      return validName(value) ? "" : "Invalid last name";
    case "emailOrPhone":
      if (value.includes("@")) {
        return validEmail(value) ? "" : "Invalid email";
      } else {
        return validPhoneNumber(value) ? "" : "Invalid phone number";
      }
    default:
      return "";
  }
};

interface FormValidationProps {
  firstNameInput: string;
  lastNameInput: string;
  emailOrPhoneInput: string;
  lenders: InviteLender[];
}

export const isFormValid = ({
  firstNameInput,
  lastNameInput,
  emailOrPhoneInput,
  lenders,
}: FormValidationProps) => {
  return (
    firstNameInput &&
    lastNameInput &&
    emailOrPhoneInput &&
    !validateField("firstName", firstNameInput) &&
    !validateField("lastName", lastNameInput) &&
    !validateField("emailOrPhone", emailOrPhoneInput) &&
    lenders.some((l) => l.enabled || l.isPrimary)
  );
};

const updateLendersToInvite = (lenders: InviteLender[]) => {
  return lenders.map((lender) => ({
    auto_submit: lender.autoSubmit,
    enabled: lender.isPrimary ? true : lender.enabled,
    lender_id: lender.id as DomainLender,
  }));
};

interface BuildInviteRequestProps {
  firstNameInput: string;
  lastNameInput: string;
  emailOrPhoneInput: string;
  lenders: InviteLender[];
}

interface SendInviteRawType {
  dealer_id: string;
  firstNameInput: string;
  lastNameInput: string;
  emailOrPhoneInput: string;
  lenders: InviteLender[];
  fetchFromApi: FetchFromApiType;
  ignoreDuplicate?: boolean;
}

/**
 * Raw invite send function that throws errors for caller to handle.
 * Useful for handling duplicate errors with custom dialogs.
 */
export const sendInviteRaw = async ({
  dealer_id,
  firstNameInput,
  lastNameInput,
  emailOrPhoneInput,
  lenders,
  fetchFromApi,
  ignoreDuplicate,
}: SendInviteRawType) => {
  const path = `${API_PREFIX_PATH}/${dealer_id}/applications/invite`;
  const body: Record<string, unknown> = {
    first_name: firstNameInput,
    last_name: lastNameInput,
    email: emailOrPhoneInput.includes("@") ? emailOrPhoneInput : "",
    phone_number: emailOrPhoneInput.includes("@")
      ? ""
      : addCountryCode(emailOrPhoneInput),
    lenders: updateLendersToInvite(lenders),
  };

  if (ignoreDuplicate) {
    body.ignore_duplicate = true;
  }

  return fetchFromApi(path, {
    method: "POST",
    body,
  });
};

export const sendInvite = ({
  firstNameInput,
  lastNameInput,
  emailOrPhoneInput,
  lenders,
}: BuildInviteRequestProps): DtoApplicationInviteReq => ({
  first_name: firstNameInput,
  last_name: lastNameInput,
  email: emailOrPhoneInput.includes("@") ? emailOrPhoneInput : "",
  phone_number: emailOrPhoneInput.includes("@")
    ? ""
    : addCountryCode(emailOrPhoneInput),
  lenders: updateLendersToInvite(lenders),
});
